import { useState } from "react";
import { Link } from "react-router-dom";
import Footer from "@/components/site/Footer";
import BookingSidebar from "@/components/site/BookingSidebar";
import hero from "@/assets/tour-cambodia-18day-monks.jpg";

const itinerary = [
  { key: "01", label: "Day 1", time: "7:30 AM – 5:30 PM", title: "Sunrise at Angkor Wat, Angkor Thom, Bayon & Ta Prohm", body: "Begin in Siem Reap with sunrise at Angkor Wat, then the south gate of Angkor Thom, the faces of the Bayon and the tree-clad ruins of Ta Prohm. Overnight: 4–5★ hotel in Siem Reap." },
  { key: "02", label: "Day 2", time: "8:00 AM – 5:30 PM", title: "Banteay Srei & Grand Circuit", body: "The pink sandstone carvings of Banteay Srei and the Grand Circuit temples. Overnight: 4–5★ hotel in Siem Reap." },
  { key: "03", label: "Day 3", time: "8:00 AM – 2:00 PM", title: "Kampong Phluk Floating Village & Tonlé Sap", body: "Boat trip to Kampong Phluk on Tonlé Sap stilt houses, flooded forest and daily life on the lake. Overnight: Siem Reap." },
  { key: "04", label: "Day 4", time: "7:30 AM – 5:30 PM", title: "Phnom Kulen National Park", body: "1000 Lingas riverbed, the Giant Buddha and the waterfall of Phnom Kulen. Overnight: Siem Reap." },
  { key: "05", label: "Day 5", time: "7:30 AM – 6:30 PM", title: "Beng Mealea & Koh Ker Temples", body: "The jungle-swallowed ruin of Beng Mealea and the seven-tiered pyramid of Koh Ker. Overnight: Siem Reap." },
  { key: "06", label: "Day 6", time: "7:30 AM – 5:30 PM", title: "Banteay Chhmar Temple & Village Homestay", body: "Drive west to remote Banteay Chhmar with a village homestay experience. Overnight: eco-lodge near Banteay Chhmar." },
  { key: "07", label: "Day 7", time: "7:00 AM – 6:00 PM", title: "Transfer to Preah Vihear", body: "Transfer to Preah Vihear and explore the UNESCO hilltop temple. Overnight: guesthouse in Preah Vihear." },
  { key: "08", label: "Day 8", time: "8:00 AM – 5:00 PM", title: "Sambor Prei Kuk & Kampong Thom", body: "Visit the pre-Angkorian temples of Sambor Prei Kuk, then continue to Kampong Thom. Overnight: hotel in Kampong Thom." },
  { key: "09", label: "Day 9", time: "8:00 AM – 6:00 PM", title: "Transfer to Battambang", body: "Transfer to Battambang with a countryside orientation. Overnight: 4★ hotel in Battambang." },
  { key: "10", label: "Day 10", time: "8:00 AM – 6:00 PM", title: "Bamboo Train, Pottery Village & Killing Caves", body: "The Bamboo Train and pottery village by day, the Killing Caves and sunset bats by evening. Overnight: Battambang." },
  { key: "11", label: "Day 11", time: "8:00 AM – 5:00 PM", title: "Countryside Cycling & Cooking Class", body: "Battambang countryside cycling and a local Khmer cooking class. Overnight: Battambang." },
  { key: "12", label: "Day 12", time: "7:30 AM – 3:00 PM", title: "Transfer to Phnom Penh", body: "Transfer to the capital, with a riverside walk and night market in the evening. Overnight: 4–5★ hotel in Phnom Penh." },
  { key: "13", label: "Day 13", time: "8:00 AM – 5:00 PM", title: "Royal Palace, National Museum & Killing Fields", body: "Royal Palace and Silver Pagoda, the National Museum and a sober afternoon at the Killing Fields. Overnight: Phnom Penh." },
  { key: "14", label: "Day 14", time: "8:00 AM – 4:00 PM", title: "Russian Market, Silk Island & Street Food Walk", body: "Russian Market, Silk Island and a street food walking tour. Overnight: Phnom Penh." },
  { key: "15", label: "Day 15", time: "7:00 AM – 5:00 PM", title: "Transfer to Mondulkiri", body: "Transfer to Mondulkiri and an afternoon exploring Sen Monorom town. Overnight: eco-lodge in Mondulkiri." },
  { key: "16", label: "Day 16", time: "8:00 AM – 5:00 PM", title: "Elephant Sanctuary & Bou Sra Waterfall", body: "Visit an ethical elephant sanctuary and trek to Bou Sra waterfall. Overnight: eco-lodge in Mondulkiri." },
  { key: "17", label: "Day 17", time: "7:30 AM – 4:00 PM", title: "Transfer to Kampot & Pepper Plantation", body: "Transfer south to Kampot and visit a famous pepper plantation. Overnight: 4★ boutique hotel in Kampot." },
  { key: "18", label: "Day 18", time: "8:00 AM – 5:00 PM", title: "Bokor Mountain, Salt Fields & Firefly Cruise", body: "Bokor Mountain day trip, then salt fields and a firefly river cruise at dusk. Overnight: Kampot." },
  { key: "19", label: "Day 19", time: "8:00 AM – 3:00 PM", title: "Kep Crab Market & Beach Transfer", body: "Kep crab market and beach, then transfer to Sihanoukville. Overnight: 4★ beach resort." },
  { key: "20", label: "Day 20", time: "Free Day", title: "Koh Rong Sanloem  to Snorkelling & Bioluminescence", body: "Ferry to Koh Rong Sanloem for snorkelling, beach time and bioluminescent plankton after dark. Overnight: beach bungalow." },
  { key: "21", label: "Day 21", time: "8:00 AM – 3:00 PM", title: "Return & Airport Transfer", body: "Return to the mainland and transfer to Phnom Penh Airport for your onward flight." },
];

const Timeline = ({ items }: { items: typeof itinerary }) => (
  <ol className="space-y-px">
    {items.map((s, idx) => (
      <li
        key={s.key}
        className="group grid grid-cols-[auto_1fr] md:grid-cols-[200px_1fr] gap-x-4 md:gap-x-12 min-w-0 border-t border-border py-8 md:py-10 transition-colors hover:bg-secondary/40 last:border-b"
      >
        <div className="flex flex-col items-start gap-y-2 text-base">
          <span className="font-display text-primary tabular-nums text-xl">
            {String(idx + 1).padStart(2, "0")}
          </span>
          <span className="uppercase tracking-[0.25em] text-primary text-xs md:text-base">
            {s.time}
          </span>
        </div>
        <div>
          <h3 className="font-display text-2xl md:text-3xl leading-tight mb-3 transition-colors group-hover:text-primary">
            {s.title}
          </h3>
          <p className="text-muted-foreground font-light leading-relaxed text-lg max-w-2xl">
            {s.body}
          </p>
        </div>
      </li>
    ))}
  </ol>
);

const optionOne = {
  label: "OPTION 1TRANSPORT ONLY",
  pricePrivate: "$2,350 – $2,670 total (1–2 pax)",
  priceGroup: "$2,350 per person",
  includes: [
    "Air-conditioned transportation",
    "Hotel pickup and drop-off",
    "Complimentary bottled water & cold towels",
    "Photography guidance",
  ],
  excludes: [
    "Professional English-speaking guide",
    "All accommodation & meals",
    "Personal expenses & travel insurance",
  ],
};

const optionTwo = {
  label: "OPTION 2 ALL-INCLUSIVE",
  pricePrivate: "$5,200 – $6,200 total (1–2 pax)",
  priceGroup: "$2,600 per person",
  includes: [
    "All accommodation — 4★ hotels & eco-lodges",
    "All entrance fees",
    "Professional English-speaking guide",
    "Air-conditioned transportation",
    "Meals (lunch) Day 2 to Day 21",
    "3 dinner shows with Khmer dance",
    "Elephant sanctuary fee",
    "Complimentary bottled water & cold towels",
  ],
  excludes: ["Tips and gratuities", "Personal expenses", "Travel insurance"],
};

const TourCambodia21Day = () => {
  const [option, setOption] = useState(0);
  const opt = option === 0 ? optionOne : optionTwo;
  return (
    <main className="bg-background">
      {/* HERO */}
      <header className="relative bg-jungle text-background overflow-hidden">
        <img
          src={hero}
          alt="Cambodia 21 day tailor-made tour with French-speaking guide — novice monks at an Angkor temple, Siem Reap"
          width={1920}
          height={1280}
          className="absolute inset-0 h-full w-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-jungle/70 via-jungle/50 to-jungle/95" />
        <div className="relative z-10">
          <div className="container flex items-center justify-between py-6">
            <Link to="/" className="flex items-center gap-2">
              <span className="font-display text-2xl tracking-wide">Aṅkor</span>
              <span className="text-[10px] uppercase tracking-[0.3em] opacity-70">Sojourns</span>
            </Link>
            <Link to="/tours" className="text-[11px] uppercase tracking-[0.3em] border-b border-background/40 pb-1 hover:border-primary hover:text-primary transition-colors">
              ← All Tours
            </Link>
          </div>
          <div className="container pt-20 pb-32 md:pt-28 md:pb-44 max-w-5xl">
            <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Twenty-One Day Complete Discovery</p>
            <h1 className="font-display text-5xl md:text-7xl leading-[0.95] text-balance lg:text-2xl">
              21 Days Across <em className="font-light">Cambodia</em> Siem Reap, Preah Vihear, Battambang, Phnom Penh, Mondulkiri, Kampot &amp; the Islands.
            </h1>
            <p className="mt-10 max-w-xl text-background/70 leading-relaxed text-xl font-light">
              The ultimate Cambodia experience over 21 days. From ancient temples and remote provinces to elephant sanctuaries in Mondulkiri, pepper plantations in Kampot, and tropical island getaways — experience every facet of this remarkable country.
            </p>
            <div className="mt-10 flex flex-wrap gap-x-10 gap-y-3 text-[11px] uppercase tracking-[0.3em] text-background/80">
              <span>Duration · 21 Days</span>
              <span>Group · 1–12</span>
              <span>From $2,350 / pax</span>
            </div>
          </div>
        </div>
      </header>

      {/* INTRO */}
      <section className="py-24 md:py-32">
        <div className="container max-w-4xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— The Idea</p>
          <h2 className="font-display text-4xl leading-[1.05] mb-10 text-balance md:text-xl">
            Every facet of Cambodia, in twenty-one unhurried days.
          </h2>
          <div className="space-y-6 text-lg font-light text-muted-foreground leading-relaxed">
            <p>
              Six days at Angkor and Tonlé Sap, the cliff-edge temple of Preah Vihear and the moated city of Banteay Chhmar, slow days in Battambang, the Royal Palace and Killing Fields of Phnom Penh, the elephant forests of Mondulkiri, the pepper coast of Kampot and Kep, and finally the bioluminescent waters of Koh Rong Sanloem.
            </p>
          </div>
        </div>
      </section>

      {/* ITINERARY */}
      <section className="pb-24 md:pb-32">
        <div className="container max-w-6xl"><div className="grid lg:grid-cols-[1fr_420px] gap-x-12 gap-y-12 items-start"><div>
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Itinerary</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-16 text-balance">
            Twenty-one days, day by day.
          </h2>
          <Timeline items={itinerary} />
        </div><BookingSidebar pricePerPerson={2350} tourTitle="21 Days Tour Across Cambodia" pricingTiers={[{ maxPax: 2, price: 2350 }, { maxPax: 6, price: 2670 }, { maxPax: 10, price: 3156 }]} /></div></div>
      </section>

      {/* PRICING TABS */}
      <section className="bg-secondary py-24 md:py-32">
        <div className="container max-w-5xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Pricing &amp; Inclusions</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-12 text-balance">
            Choose your level of comfort.
          </h2>

          <div role="tablist" aria-label="Pricing options" className="flex flex-wrap justify-center gap-10 md:gap-16 mb-16">
            {[optionOne, optionTwo].map((o, i) => {
              const isActive = i === option;
              return (
                <button
                  key={o.label}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setOption(i)}
                  className={`relative pb-3 uppercase tracking-[0.3em] transition-colors text-base ${
                    isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {o.label}
                  <span
                    className={`absolute left-0 right-0 -bottom-px h-px transition-colors ${
                      isActive ? "bg-primary" : "bg-transparent"
                    }`}
                  />
                </button>
              );
            })}
          </div>

          <ul className="mb-12 space-y-4 font-light text-lg max-w-3xl mx-auto">
            <li className="border-b border-border pb-4 flex justify-between gap-6"><span>Private Tour (21 Days)</span><span className="text-right">{opt.pricePrivate}</span></li>
            <li className="pb-4 flex justify-between gap-6"><span>Group Tour</span><span className="text-right">{opt.priceGroup}</span></li>
          </ul>

          <div className="grid md:grid-cols-2 gap-x-12 gap-y-12">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Includes</p>
              <ul className="space-y-4 font-light text-lg">
                {opt.includes.map((x) => (
                  <li key={x} className="border-b border-border pb-4 last:border-b-0">{x}</li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Excludes</p>
              <ul className="space-y-4 font-light text-lg">
                {opt.excludes.map((x) => (
                  <li key={x} className="border-b border-border pb-4 last:border-b-0">{x}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT TO BRING */}
      <section className="py-24 md:py-32">
        <div className="container max-w-5xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Essential Items</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-16 text-balance">What to bring, what to leave behind.</h2>
          <div className="grid md:grid-cols-3 gap-x-10 gap-y-12">
            <div>
              <h3 className="font-display text-2xl mb-5">Required</h3>
              <ul className="space-y-3 font-light text-muted-foreground">
                <li>Modest clothing for temples</li>
                <li>Comfortable walking / hiking shoes</li>
                <li>Swimwear &amp; insect repellent</li>
                <li>Light rain jacket</li>
              </ul>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-5">Recommended</h3>
              <ul className="space-y-3 font-light text-muted-foreground">
                <li>Camera with extra batteries</li>
                <li>Small daypack</li>
                <li>Reusable water bottle</li>
                <li>Sun protection hat, sunscreen</li>
              </ul>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-5">Leave at Hotel</h3>
              <ul className="space-y-3 font-light text-muted-foreground">
                <li>Passport (bring a copy)</li>
                <li>Airline tickets</li>
                <li>Excess cash</li>
                <li>Valuable jewellery</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* GOOD TO KNOW */}
      <section className="bg-secondary py-24 md:py-32">
        <div className="container max-w-4xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Good to Know</p>
          <div className="space-y-6 text-lg font-light text-muted-foreground leading-relaxed">
            <p><span className="text-foreground">Weather.</span> Varied climate across regions hot in the lowlands, cooler in the Mondulkiri highlands and breezy near the islands.</p>
            <p><span className="text-foreground">Food.</span> Diverse regional cuisine fresh seafood on the coast, Kampot pepper specialties, and traditional Khmer dishes throughout.</p>
            <p>Pickup time: 7:00 – 8:00 AM (exact time confirmed one day before).</p>
            <p>Dress code: casual but respectful in temples. Some hiking is involved in Mondulkiri (moderate fitness required).</p>
            <p>Children under 5 are not permitted. Maximum group size: 1–12 travellers.</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32">
        <div className="container max-w-3xl text-center">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Reserve Your Journey</p>
          <h2 className="font-display text-4xl md:text-6xl leading-[1.05] mb-10 text-balance">
            Twenty-one days. From Angkor to the elephants to the islands.
          </h2>
          <Link
            to="/#contact"
            className="inline-block bg-foreground text-background px-10 py-4 text-[11px] uppercase tracking-[0.3em] hover:bg-primary transition-colors"
          >
            Book Now →
          </Link>
        </div>
      </section>
      <Footer />
    </main>
  );
};

export default TourCambodia21Day;
